/**
 * Pinia store for customer data management
 *
 * Follows sde-client pattern: direct API calls in store actions.
 * FE uses nested structure matching BE:
 * - Customer { person, company? }
 * - person: Always populated (for PERSON = the person, for COMPANY = responsible person)
 * - company: Only for COMPANY type
 *
 * The optional "Kontaktní osoba" lives in the contactPerson* flat fields on Person.
 *
 * BE API Namespace: api.customers
 */

import { defineStore } from 'pinia'
import {
  type Customer,
  type CustomerType,
  type Person,
  type Company,
  type CustomerInvoice,
  type DocumentEvent,
  type CreateInvoiceRequest,
  type UpdateInvoiceRequest,
  type PortionType,
  createEmptyPerson
} from '~/api/types/customer'
import { buildPaymentPortions, syncPaidFlag, ensurePayments } from '~/utils/invoicePaymentUtils'

/**
 * Normalize IČO from BE — BE may store as number, losing leading zeros.
 * Always pad to 8 digits for display.
 */
const normalizeIco = (company: Company): void => {
  if (company.companyBusinessNumber) {
    company.companyBusinessNumber = String(company.companyBusinessNumber).padStart(8, '0')
  }
}

/**
 * Parse customData from BE response (may be JSON string or object)
 */
const parseCustomData = (customer: Customer): Customer => {
  if (customer.customData && typeof customer.customData === 'string') {
    try {
      customer.customData = JSON.parse(customer.customData)
    } catch (e) {
      console.warn('[customerStore] Failed to parse customData:', e)
      customer.customData = undefined
    }
  }
  return customer
}

/**
 * Transform BE response to Customer array
 * BE returns: { persons: [...], companies: [...] }
 * Each company entry has: { [companyName]: companyData, contactPerson: personData, type: "Map" }
 * The BE's "contactPerson" key is historically misleading — it carries the main responsible person.
 */
const transformCustomersResponse = (response: Record<string, unknown>): Customer[] => {
  const customers: Customer[] = []

  // Process persons - each becomes a PERSON type customer
  const persons = response.persons as Record<string, unknown>[] | undefined
  if (Array.isArray(persons)) {
    persons.forEach(p => {
      const customer: Customer = {
        id: p.id as string,
        type: 'PERSON',
        person: p as unknown as Person,
        invoices: [],
        groupId: (p.groupId as string) ?? null,
        customData: p.customData as Customer['customData']
      }
      customers.push(parseCustomData(customer))
    })
  }

  // Process companies - BE returns as array of objects with company name as key
  const companies = response.companies as Record<string, unknown>[] | undefined
  if (Array.isArray(companies)) {
    companies.forEach(companyObj => {
      let companyData: Company | undefined
      let companyName: string | undefined
      let mainPersonData: Person | undefined

      // Extract company data and the main person (returned by BE as "contactPerson")
      Object.entries(companyObj).forEach(([key, value]) => {
        if (key === 'contactPerson' && value && typeof value === 'object') {
          mainPersonData = value as unknown as Person
        } else if (key !== 'type' && typeof value === 'object' && value !== null) {
          // This is the company data (keyed by company name)
          companyName = key
          companyData = value as unknown as Company
        }
      })

      if (companyData) {
        // Set company name from the key if not already present in data
        if (companyName && !companyData.name) {
          companyData.name = companyName
        }
        normalizeIco(companyData)

        const customer: Customer = {
          id: companyData.id,
          type: 'COMPANY',
          person: mainPersonData,
          company: companyData,
          invoices: [],
          groupId: (companyData.groupId as string) ?? (mainPersonData?.groupId as string) ?? null,
          customData: companyData.customData as Customer['customData']
        }
        customers.push(parseCustomData(customer))
      }
    })
  }

  return customers
}

export const useCustomerStore = defineStore({
  id: 'customers',

  state: () => ({
    customers: {} as Record<string, Customer>,
    loading: false,
    initialized: false
  }),

  getters: {
    allCustomers: (state) => Object.values(state.customers),
    customer: (state) => (id: string) => state.customers[id]
  },

  actions: {
    /**
     * Fetch all customers from API
     */
    async fetchCustomers(api: API) {
      if (!api?.customers) {
        console.warn('[customerStore] api.customers not available')
        return
      }

      this.loading = true

      try {
        const response = await api.customers.getAllCustomers()
        const fetched = transformCustomersResponse(response as Record<string, unknown>)

        this.$patch(() => {
          fetched.forEach((c: Customer) => {
            const existing = this.customers[c.id]
            this.customers[c.id] = {
              ...c,
              invoices: existing?.invoices ?? c.invoices
            }
          })
          this.initialized = true
        })
      } catch (e) {
        console.error('[customerStore] Failed to fetch customers:', e)
      } finally {
        this.loading = false
      }
    },

    /**
     * Get customer by Person/Company ID from API.
     * Store keys are Person/Company IDs, so we use getPersonOrCompanyById
     * (api.customers.get expects the Customer entity ID which is different).
     */
    async get(api: API, id: string): Promise<Customer | null> {
      if (!api?.customers) {
        console.warn('[customerStore] api.customers not available')
        return null
      }

      const response = await api.customers.getPersonOrCompanyById(id)
      const raw = response as Record<string, unknown>

      let customer: Customer

      if ('companyBusinessNumber' in raw) {
        const companyData = raw as unknown as Company
        normalizeIco(companyData)
        customer = {
          id: raw.id as string,
          type: 'COMPANY',
          person: raw.contactPerson as Person | undefined,
          company: companyData,
          groupId: (raw.groupId as string) ?? null,
          customData: raw.customData as Customer['customData']
        }
      } else {
        customer = {
          id: raw.id as string,
          type: 'PERSON',
          person: raw as unknown as Person,
          groupId: (raw.groupId as string) ?? null,
          customData: raw.customData as Customer['customData']
        }
      }

      customer = parseCustomData(customer)

      this.$patch(() => {
        this.customers[customer.id] = customer
      })

      return customer
    },

    /**
     * Create a new customer
     * BE expects: create(type, data, customData)
     * - PERSON: { person: {...} }
     * - COMPANY: { person: {...}, company: {...} }
     *   person = main responsible person on the company; "Kontaktní osoba"
     *            lives in its contactPerson* flat fields when present.
     */
    async create(api: API, customer: Omit<Customer, 'id'>): Promise<Customer> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      this.loading = true

      try {
        const type = customer.type || 'PERSON'
        let newCustomer: Customer
        let data: Record<string, unknown>

        if (type === 'PERSON') {
          data = { person: customer.person || createEmptyPerson(),
          groupId: customer.groupId ?? null
          }
        } else {
          data = {
            person: customer.person || createEmptyPerson(),
            company: customer.company || {},
            groupId: customer.groupId ?? null

          }
        }

        const response = await api.customers.create(type, data, null)
        const newId = (response as Record<string, unknown>).id as string

        // Build customer from response - don't fetch immediately as BE may not be ready
        if (type === 'PERSON') {
          newCustomer = {
            id: newId,
            type: 'PERSON',
            person: response as unknown as Person,
            invoices: [],
            groupId: customer.groupId ?? null,
          }
        } else {
          const createdCompany = (response as Record<string, unknown>).company as Company || response as unknown as Company
          newCustomer = {
            id: newId,
            type: 'COMPANY',
            person: (response as Record<string, unknown>).person as Person || customer.person,
            company: createdCompany,
            invoices: [],
            groupId: customer.groupId ?? null,
          }
        }

        // Add to local state
        this.customers[newId] = newCustomer

        return newCustomer
      } catch (e) {
        console.error('[customerStore] Failed to create customer:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Update an existing customer
     * BE expects: update(id, type, data, customData)
     * - PERSON: { person: {...} }
     * - COMPANY: { person: {...}, company: {...} }
     *   person = responsible person for the company
     */
    async update(api: API, id: string, updates: Partial<Customer>): Promise<Customer | null> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      this.loading = true

      try {
        // Ensure the customer is in the store with full person/company data.
        // fetchCustomers → transformCustomersResponse is the only reliable path
        // that resolves both company data and the associated contactPerson.
        // get() only returns the raw Person/Company entity without relationships.
        let existing = this.customers[id]
        if (!existing?.person) {
          await this.fetchCustomers(api)
          existing = this.customers[id]
        }

        const merged = { ...existing, ...updates }
        const type = merged.type || 'PERSON'

        // Extract customData for BE API (may contain documents, containerId, etc.)
        // Stringify for BE storage (will be parsed back when fetching)
        const customData = merged.customData ? JSON.stringify(merged.customData) : null

        // Always send full person/company data from the store.
        // The BE requires person data on every update call and uses
        // identificationCardNumber as the persons table PRIMARY KEY.
        // Sending the existing stored values preserves the correct PK.
        const person: Record<string, unknown> = { ...(merged.person || createEmptyPerson()) }

        // Defensive: BE AbstractPerson was missing getter for identificationCardNumber,
        // so API responses may not include it. If missing/empty, use the person's UUID
        // as a unique fallback to prevent duplicate PK constraint violations.
        if (!person.identificationCardNumber && id) {
          console.warn('[customerStore] identificationCardNumber missing, using ID as fallback:', id)
          person.identificationCardNumber = id
        }

        let data: Record<string, unknown>

        if (type === 'PERSON') {
          data = { person, groupId: merged.groupId ?? null }
        } else {
          data = {
            person,
            company: merged.company || {},
            groupId: merged.groupId ?? null
          }
        }

        await api.customers.update(id, type, data, customData)

        // Re-fetch all customers to ensure state consistency.
        // getPersonOrCompanyById doesn't return contactPerson or full company relationships,
        // so we use fetchCustomers (getAllCustomers) which correctly resolves everything.
        await this.fetchCustomers(api)

        return this.customers[id] || null
      } catch (e) {
        console.error('[customerStore] Failed to update customer:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Delete a customer
     * The FE stores Person/Company ID as customer.id, but the BE delete
     * expects the Customer entity ID. We resolve it via getCustomerByCustomerId first.
     */
    async delete(api: API, id: string): Promise<boolean> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      this.loading = true

      try {
        // Resolve the Customer entity ID from Person/Company ID
        const customerEntity = await api.customers.getCustomerByCustomerId(id)
        const customerEntityId = (customerEntity as Record<string, unknown>).id as string

        if (!customerEntityId) {
          throw new Error(`Could not resolve Customer entity for id: ${id}`)
        }

        await api.customers.delete(customerEntityId)

        this.$patch(() => {
          delete this.customers[id]
        })

        return true
      } catch (e) {
        console.error('[customerStore] Failed to delete customer:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    // ==================== Invoice Operations ====================

    /**
     * Fetch invoices for a customer
     */
    async fetchInvoices(api: API, customerId: string): Promise<CustomerInvoice[]> {
      if (!api?.customers) {
        console.warn('[customerStore] api.customers not available')
        return []
      }

      try {
        const response = await api.customers.getInvoicesByCustomerId(customerId)
        const rawInvoices = (response.value || response || []) as CustomerInvoice[]

        // Parse customData if it's a JSON string
        const invoices = rawInvoices.map(inv => {
          if (inv.customData && typeof inv.customData === 'string') {
            try {
              inv.customData = JSON.parse(inv.customData)
            } catch (e) {
              console.warn('[customerStore] Failed to parse invoice customData:', e)
            }
          }
          return inv
        })

        this.$patch(() => {
          if (this.customers[customerId]) {
            this.customers[customerId].invoices = invoices
          }
        })

        return invoices
      } catch (e) {
        console.error('[customerStore] Failed to fetch invoices:', e)
        throw e
      }
    },

    /**
     * Create an invoice with payment portions.
     *
     * customerType determines how portions are built:
     * - COMPANY: taxBase (insurance) + vat (company) — except POLICE_TICKET
     * - PERSON: single 'full' portion
     *
     * If customData already contains payments[], they are used as-is.
     * Otherwise, buildPaymentPortions() generates them from cost + customerType.
     */
    async createInvoice(
      api: API,
      customerId: string,
      invoice: CreateInvoiceRequest & { customData?: CustomerInvoice['customData'] },
      customerType?: CustomerType
    ): Promise<CustomerInvoice> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      try {
        // Resolve customer type if not provided
        const resolvedType = customerType ?? this.customers[customerId]?.type ?? 'PERSON'

        // Build payment portions if not already present
        const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
        if (!customData.payments?.length) {
          const { payments, amountWithoutVat, vatRate, vatAmount } = buildPaymentPortions(
            invoice.cost,
            resolvedType,
            invoice.invoiceType,
            {
              amountWithoutVat: customData.amountWithoutVat as number | undefined,
              vatAmount: customData.vatAmount as number | undefined
            }
          )
          customData.payments = payments
          if (amountWithoutVat != null) customData.amountWithoutVat = amountWithoutVat
          if (vatRate != null) customData.vatRate = vatRate
          if (vatAmount != null) customData.vatAmount = vatAmount
        }
        if (invoice.paid) {
          const datePaid = (customData.datePaid as string) || (customData.invoiceDate as string) || new Date().toISOString().split('T')[0]
          customData.payments = customData.payments.map(p => ({
            ...p, paid: true, paidAmount: p.amount, datePaid: p.datePaid || datePaid
          }
          ))
        }

        const customDataStr = JSON.stringify(customData)

        const response = await api.customers.createInvoice(
          invoice.invoiceId,
          invoice.invoiceType,
          invoice.cost,
          invoice.paid,
          customerId,
          customDataStr as unknown as Record<string, unknown> | null
        )
        const newInvoice = response as CustomerInvoice

        // Fetch fresh invoices from BE to ensure state consistency
        await this.fetchInvoices(api, customerId)

        try {
          const customer = this.customers[customerId]
          const isTaxPayer = !!customer?.company?.taxPayer

          if (isTaxPayer) {

            api.notifications.createNotification('dphRequest', customerId, null, null, null, { invoiceId: customData.contentId })

            .then(() => console.log('dphRequest notification sent'))
            .catch((err: Error) => console.error('dphRequest failed:', err ))
          }

        } catch (e) {
          console.warn('Failed to send dphRequest:', e)
        }

        // Internal
        try {
           api.notifications.createNotificationEvent('INVOICE_UPLOAD', null, null,
            {
              invoiceId: newInvoice.invoiceId
            }
           )
           .then(() => console.log('[customerStore] INVOICE_UPLOAD notification sent'))
           .catch((err: Error) => console.error('INVOICE_UPLOAD failed:', err) )
        } catch (e) {
          console.warn('Failed to send INVOICE_UPLOAD notification:', e)
        }
        return newInvoice
      } catch (e) {
        console.error('[customerStore] Failed to create invoice:', e)
        throw e
      }
    },

    /**
     * Update an invoice. Syncs top-level `paid` from payments[] before saving.
     */
    async updateInvoice(
      api: API,
      invoiceId: string,
      customerId: string,
      updates: UpdateInvoiceRequest & { customData?: CustomerInvoice['customData'] }
    ): Promise<CustomerInvoice> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      try {
        // Sync paid flag from payments before saving
        const customData = updates.customData ? { ...updates.customData } : undefined
        let paid = updates.paid || false
        if (customData?.payments?.length) {
          const inv = { paid, customData } as CustomerInvoice
          paid = syncPaidFlag(inv)
        }

        const customDataStr = customData ? JSON.stringify(customData) : null

        const response = await api.customers.updateInvoice(
          invoiceId,
          updates.invoiceId || '',
          updates.invoiceType || 'RENT',
          updates.cost || 0,
          paid,
          customerId,
          customDataStr as unknown as Record<string, unknown> | null
        )
        const updatedInvoice = response as CustomerInvoice

        // Fetch fresh invoices from BE to ensure state consistency
        await this.fetchInvoices(api, customerId)

        return updatedInvoice
      } catch (e) {
        console.error('[customerStore] Failed to update invoice:', e)
        throw e
      }
    },

    /**
     * Record a payment on a single portion.
     *
     * - paidAmount === effective amount (amount - creditNote): marks portion as fully paid
     * - paidAmount < effective: records partial payment, portion stays unpaid
     * - paidAmount === 0 or undefined with paid=false: revert to unpaid (clears paidAmount)
     */
    async recordPortionPayment(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      portionType: PortionType,
      paidAmount: number,
      datePaid?: string
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      const idx = payments.findIndex(p => p.type === portionType)
      if (idx === -1) throw new Error(`Portion type "${portionType}" not found on invoice ${invoice.id}`)

      const portion = payments[idx]
      const effectiveAmount = portion.amount - (portion.creditNoteAmount ?? 0)
      const isFullPayment = paidAmount >= effectiveAmount

      payments[idx] = {
        ...portion,
        paidAmount: paidAmount > 0 ? paidAmount : undefined,
        paid: isFullPayment,
        datePaid: paidAmount > 0
          ? (datePaid || portion.datePaid || new Date().toISOString().split('T')[0])
          : undefined
      }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        customData
      })
    },

    /**
     * Revert a portion to unpaid (clears paidAmount and datePaid).
     */
    async revertPortionPayment(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      portionType: PortionType
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      const idx = payments.findIndex(p => p.type === portionType)
      if (idx === -1) throw new Error(`Portion type "${portionType}" not found on invoice ${invoice.id}`)

      payments[idx] = {
        ...payments[idx],
        paid: false,
        paidAmount: undefined,
        datePaid: undefined
      }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        customData
      })
    },

    /**
     * Record a payment request on a single payment portion.
     */
    async requestPortionPayment(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      portionType: PortionType
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      const idx = payments.findIndex(p => p.type === portionType)
      if (idx === -1) throw new Error(`Portion type "${portionType}" not found on invoice ${invoice.id}`)

      const existingDates = payments[idx].paymentRequestDates || []
      payments[idx] = {
        ...payments[idx],
        paymentRequestDates: [...existingDates, new Date().toISOString().split('T')[0]]
      }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        paid: invoice.paid,
        customData
      })
    },

    async applyCreditNote(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      data: {
        creditNoteNumber: string
        creditNoteAmount: number
        creditNoteReason?: string
        portionType?: PortionType
      }
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      if (!payments.length) {
        throw new Error(`Invoice ${invoice.id} has no payment portions to credit`)
      }

      const targetType: PortionType = data.portionType
        ?? (payments.find(p => p.type === 'taxBase')?.type ?? payments[0].type)
      const idx = payments.findIndex(p => p.type === targetType)
      if (idx === -1) throw new Error(`Portion type "${targetType}" not found on invoice ${invoice.id}`)

      const portion = payments[idx]
      const newEffective = portion.amount - data.creditNoteAmount
      const paidAmount = portion.paidAmount ?? 0
      const fullyPaid = portion.paid || (newEffective - paidAmount) <= 0

      payments[idx] = {
        ...portion,
        creditNoteAmount: data.creditNoteAmount,
        creditNoteNumber: data.creditNoteNumber,
        creditNoteReason: data.creditNoteReason,
        paid: fullyPaid,
        datePaid: fullyPaid
          ? (portion.datePaid || new Date().toISOString().split('T')[0])
          : portion.datePaid
      }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        customData
      })
    },

    async removeCreditNote(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      portionType?: PortionType
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      const targetType: PortionType | undefined = portionType
        ?? payments.find(p => p.creditNoteAmount != null)?.type
      if (!targetType) return invoice

      const idx = payments.findIndex(p => p.type === targetType)
      if (idx === -1) throw new Error(`Portion type "${targetType}" not found on invoice ${invoice.id}`)

      const portion = payments[idx]
      const paidAmount = portion.paidAmount ?? 0
      const fullyPaid = portion.paid && paidAmount >= portion.amount

      payments[idx] = {
        ...portion,
        creditNoteAmount: undefined,
        creditNoteNumber: undefined,
        creditNoteReason: undefined,
        paid: fullyPaid,
        datePaid: fullyPaid ? portion.datePaid : undefined
      }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        customData
      })
    },

    /**
     * Update the payment date on a single portion.
     */
    async updatePortionDatePaid(
      api: API,
      invoice: CustomerInvoice,
      customerId: string,
      portionType: PortionType,
      datePaid: string
    ): Promise<CustomerInvoice> {
      const customData = { ...invoice.customData } as NonNullable<CustomerInvoice['customData']>
      const payments = [...(customData.payments || [])]
      const idx = payments.findIndex(p => p.type === portionType)
      if (idx === -1) throw new Error(`Portion type "${portionType}" not found on invoice ${invoice.id}`)

      payments[idx] = { ...payments[idx], datePaid }
      customData.payments = payments

      return this.updateInvoice(api, invoice.id, customerId, {
        invoiceId: invoice.invoiceId,
        invoiceType: invoice.invoiceType,
        cost: invoice.cost,
        paid: invoice.paid,
        customData
      })
    },

    /**
     * Delete an invoice
     */
    async deleteInvoice(api: API, invoiceId: string): Promise<boolean> {
      if (!api?.customers) {
        throw new Error('Customer API not available')
      }

      try {
        await api.customers.deleteInvoice(invoiceId)

        this.$patch(() => {
          for (const customer of Object.values(this.customers)) {
            if (customer.invoices) {
              const idx = customer.invoices.findIndex(inv => inv.id === invoiceId)
              if (idx !== -1) {
                customer.invoices.splice(idx, 1)
                break
              }
            }
          }
        })

        return true
      } catch (e) {
        console.error('[customerStore] Failed to delete invoice:', e)
        throw e
      }
    },

    // ==================== DocumentEvent Operations ====================

    /**
     * Create a document event (link invoice to SDE document)
     */
    async createDocumentEvent(
      api: API,
      invoiceId: string,
      entityId: string,
      customData?: Record<string, unknown>
    ): Promise<DocumentEvent | null> {

    }
  }
})
